import { line, curveLinear } from 'd3-shape';
import { wetAdiabaticSlope } from './moist.js';

/**
 * Attach Skysight-style cursor interactions on mousemove inside the overlay:
 *
 *   1. cursor-line — horizontal dashed gray line at the cursor's Y (the
 *      pressure level being inspected). Helps the user read off isobars.
 *   2. focusIsohume — gray line traced from the cursor pressure UPWARD along
 *      a constant water-vapor mixing-ratio curve (an "isohume"). Tells the
 *      pilot what dewpoint a parcel with the cursor's specific humidity
 *      would have at any higher altitude.
 *   3. focusSaturatedAdiabat — pink animated dashed line traced from the
 *      cursor's environment temperature UPWARD along a moist adiabat (parcel
 *      ascending while saturated). CSS class `skewt-ext-hover-adiabat`
 *      already applies the marching-ants animation (see skewt.css).
 *
 * Source: docs/skysight-skewt-bundle.js — focusIsohume and focusSaturatedAdiabat
 * paths drawn at instantiation and `attr('d', …)` updated on every mousemove.
 *
 * @param {import('d3-selection').Selection} container
 * @param {import('../skewt/scales.js').Scales} scales
 * @param {Array<{press, temp, dwpt}>} data
 */
export function attachHoverTrace(container, scales, data) {
    // Idempotent cleanup
    container.selectAll('.skewt-ext-hover-trace').remove();
    // Remove any previously-attached native listeners on the container node
    const containerNode = container.node();
    if (containerNode && containerNode.__hoverTraceCleanup) {
        containerNode.__hoverTraceCleanup();
        containerNode.__hoverTraceCleanup = null;
    }

    if (!data || data.length < 2) return;

    // We attach listeners to the CONTAINER (not the overlay rect) because the
    // lib's #render() / #drawData() pipeline destroys and recreates the
    // overlay rect on every re-render — listeners attached to the overlay get
    // lost. The container `<g>` survives all re-renders.
    if (!containerNode) return;

    const traceLayer = container.append('g').attr('class', 'skewt-ext-hover-trace');

    const basep = scales.basep;
    const topp = scales.topp || 150;
    const xLeft = scales.x(scales.tMin);
    const xRight = scales.x(scales.tMax);

    // --- Bolton 1980 saturation vapor pressure (hPa) ---
    const es = (T) => 6.112 * Math.exp((17.67 * T) / (T + 243.5));
    // Inverse: solve es(T)=e for T given e
    const tdFromEs = (e) => {
        const ln = Math.log(e / 6.112);
        return (243.5 * ln) / (17.67 - ln);
    };

    // --- Isohume: dewpoint at pressure p' for parcel starting at (T0, p0) ---
    // Mixing ratio r ∝ e/(p-e). For e<<p, r ∝ e/p. So e(p') = e(p0) * (p'/p0).
    // Then Td(p') = invert es(e(p')).
    const isohumeT = (Td0, p0, p) => tdFromEs(es(Td0) * (p / p0));

    // --- Persistent pre-allocated path elements (Skysight-style: append once,
    //     update `d` on every mousemove for performance) ---
    const cursorLine = traceLayer
        .append('line')
        .attr('class', 'skewt-ext-hover-cursor-line')
        .attr('x1', xLeft)
        .attr('x2', xRight)
        .style('stroke', 'rgba(150, 150, 150, 0.5)')
        .style('stroke-width', 1)
        .style('stroke-dasharray', '3, 3')
        .style('display', 'none')
        .style('pointer-events', 'none');

    const isohumePath = traceLayer
        .append('path')
        .attr('class', 'skewt-ext-hover-isohume')
        .style('fill', 'none')
        .style('stroke', 'rgba(120, 120, 120, 0.9)')
        .style('stroke-width', 1)
        .style('display', 'none')
        .style('pointer-events', 'none');

    // Dry adiabat (solid lightgray) — passes EXACTLY through the cursor.
    // It's anchored to cursor; the dashed moist adiabat below moves based
    // on the LCL implied by this dry adiabat + the isohume.
    const dryPath = traceLayer
        .append('path')
        .attr('class', 'skewt-ext-hover-dry-adiabat')
        .style('fill', 'none')
        .style('stroke', 'lightgray')
        .style('stroke-width', 1)
        .style('display', 'none')
        .style('pointer-events', 'none');

    // Moist adiabat (dashed pink, animated) — starts at the LCL (NOT at the
    // cursor) and traces upward. The LCL is the intersection of the cursor's
    // dry adiabat and isohume. So this line "moves freely" relative to the
    // cursor — its start point depends on cursor T+Td projected to surface.
    const moistPath = traceLayer
        .append('path')
        .attr('class', 'skewt-ext-hover-adiabat')
        .style('fill', 'none')
        .style('stroke', 'pink')
        .style('stroke-width', 2)
        .style('stroke-dasharray', 10)
        .style('display', 'none')
        .style('pointer-events', 'none');

    // Mouse CAPE / CIN translucent polygons — visualize, for the parcel
    // ascending from the cursor along a moist adiabat, where it would be
    // BUOYANT (CAPE, very faint red) and where it would be COOLER than the
    // environment (CIN, very faint blue). Source: Skysight bundle's
    // drawMouseCapeArea(parcelPath): walks the moist trace, interpolates env
    // temp at each pressure, classifies CAPE/CIN, builds polygons closed by
    // the env path reversed, fills with rgba(255,0,0,0.025) / rgba(0,0,255,0.025).
    // Skysight live DOM (verified 2026-05-27):
    //   .cape-area.mouse-cape  fill: rgba(255, 0, 0, 0.024)
    //   .cin-area.mouse-cin    fill: rgba(0, 0, 255, 0.024)
    const mouseCapeArea = traceLayer
        .append('path')
        .attr('class', 'skewt-ext-hover-cape-area')
        .style('fill', 'rgba(255, 0, 0, 0.024)')
        .style('stroke', 'none')
        .style('display', 'none')
        .style('pointer-events', 'none');
    const mouseCinArea = traceLayer
        .append('path')
        .attr('class', 'skewt-ext-hover-cin-area')
        .style('fill', 'rgba(0, 0, 255, 0.024)')
        .style('stroke', 'none')
        .style('display', 'none')
        .style('pointer-events', 'none');

    const lineGen = line()
        .x((d) => scales.skewX(d.t, d.p))
        .y((d) => scales.y(d.p))
        .curve(curveLinear);

    const STEP = 5;       // hPa step
    const POISSON = 2 / 7;

    // --- Pure dry-adiabat trace through the cursor (basep → topp) ---
    // The parcel preserves potential temperature along this curve:
    //   T(p) = T_cursor * (p / p_cursor)^POISSON  (in Kelvin)
    // At p = p_cursor this returns T_cursor exactly, so the line passes
    // through the cursor.
    const traceDry = (T0, p0) => {
        const T0K = T0 + 273.15;
        const points = [];
        for (let p = basep; p > topp; p -= STEP) {
            const Tp = T0K * Math.pow(p / p0, POISSON) - 273.15;
            points.push({ t: Tp, p });
        }
        return points;
    };

    // --- Moist-adiabat trace starting at LCL (basep → topp) ---
    // Decoded from docs/skysight-skewt-bundle.js saturatedDryAdiabat(t, s):
    //   1. l = (s+273.15) * (p0/t)^0.286 - 273.15
    //      → surface T inferred by dry-adiabatic descent from cursor.
    //   2. n = this.indexes.td0Isohume
    //      → surface Td inferred via the isohume from cursor T.
    //   3. a = this.getTlcl(l, n)                    // LCL temp (cursor inputs)
    //   4. u = this.getPlcl(this.indexes.p0, this.indexes.t0, a)
    //      → LCL pressure uses the ACTUAL data-surface T (`t0`), NOT `l`.
    //      This is the key: the moist line does NOT pass through the cursor,
    //      it's anchored to where the data-surface dry adiabat reaches `a`.
    //   5. Below LCL (m >= u): dry adiabat from `l` (cursor projection).
    //      Above LCL (m < u): moist adiabat from `a`.
    const traceMoist = (T0, p0) => {
        const T0K = T0 + 273.15;
        // (1) Cursor-projected surface T (l)
        const lK = T0K * Math.pow(basep / p0, POISSON);
        const l = lK - 273.15;
        // (2) Cursor-projected surface Td (n) via isohume
        const n = isohumeT(T0, p0, basep);
        // (3) LCL temp via Bolton 1980 eq.15 using cursor projections
        const nK = n + 273.15;
        const a1 = 1 / (nK - 56);
        const b1 = Math.log(lK / nK) / 800;
        const aTlclK = 1 / (a1 + b1) + 56;
        const aTlcl = aTlclK - 273.15;
        // (4) LCL pressure via Poisson, using the ACTUAL data-surface T (t0):
        //     u = basep * (aTlcl / t0)^(1/0.286)
        const t0 = data[0].temp;
        const t0K = t0 + 273.15;
        const u = basep * Math.pow(aTlclK / t0K, 1 / POISSON);

        const points = [];
        let T = aTlcl;
        for (let p = basep; p > topp; p -= STEP) {
            if (p >= u) {
                // Below LCL: dry adiabat from cursor-projected surface T `l`
                T = lK * Math.pow(p / basep, POISSON) - 273.15;
            } else {
                T = T - wetAdiabaticSlope(T, p - STEP / 2) * STEP;
            }
            points.push({ t: T, p });
        }
        return points;
    };

    // --- Isohume trace from SURFACE up to CURSOR (constant mixing ratio) ---
    // Skysight ends the focusIsohume path at the cursor pressure — it shows
    // "what dewpoint would this parcel have if descended to surface" but
    // does NOT extend above the cursor. The moist adiabat handles the
    // ascent portion.
    const traceIsohume = (Td0, p0) => {
        const points = [];
        for (let p = basep; p > p0; p -= STEP) {
            points.push({ t: isohumeT(Td0, p0, p), p });
        }
        // Anchor the line exactly at the cursor point.
        points.push({ t: Td0, p: p0 });
        return points;
    };

    // --- Interpolate environment temp at any pressure (descending data) ---
    const envTempAt = (p) => {
        for (let i = 0; i < data.length - 1; i++) {
            const a = data[i];
            const b = data[i + 1];
            if (p <= a.press && p >= b.press) {
                const f = (a.press - p) / (a.press - b.press);
                return a.temp + f * (b.temp - a.temp);
            }
        }
        if (p > data[0].press) return data[0].temp;
        return data[data.length - 1].temp;
    };

    // --- Build CAPE/CIN polygons from cursor's moist trace vs env ---
    // Walk the trace, classify each point as CAPE (parcel > env) or CIN, and
    // build polygons closed by the env path reversed. Source: Skysight
    // bundle's drawMouseCapeArea.
    const buildCapeAreas = (trace) => {
        const segs = [];
        let current = null;
        let prevType = null;
        for (const pt of trace) {
            if (pt.p > basep - 10) continue;
            const envT = envTempAt(pt.p);
            if (envT === null) continue;
            const type = pt.t > envT ? 'CAPE' : 'CIN';
            if (type !== prevType) {
                if (current) {
                    const cross = { t: envT, p: pt.p };
                    current.parcelPath.push(cross);
                    current.envPath.push(cross);
                    segs.push(current);
                }
                current = { type, parcelPath: [], envPath: [] };
                const cross = { t: envT, p: pt.p };
                current.parcelPath.push(cross);
                current.envPath.push(cross);
            }
            current.parcelPath.push(pt);
            current.envPath.push({ t: envT, p: pt.p });
            prevType = type;
        }
        if (current) segs.push(current);
        return segs;
    };

    const segToPolygon = (seg) => {
        const pts = [
            ...seg.parcelPath,
            ...seg.envPath.slice().reverse(),
        ];
        if (pts.length < 3) return '';
        const parts = pts.map(
            ({ t, p }) => `${scales.skewX(t, p)},${scales.y(p)}`,
        );
        return `M${parts[0]} L${parts.slice(1).join(' L')} Z`;
    };

    // --- Mouse handlers (attached to the container node, native API) ---
    const hideAll = () => {
        cursorLine.style('display', 'none');
        isohumePath.style('display', 'none');
        dryPath.style('display', 'none');
        moistPath.style('display', 'none');
        mouseCapeArea.style('display', 'none');
        mouseCinArea.style('display', 'none');
    };

    const onMove = (event) => {
        // Convert mouse client coords to chart-local (container origin).
        // We can't use containerNode.getBoundingClientRect() because the
        // container <g> includes children (altitude labels at x=-9, wind
        // axis past w, etc.) so its bbox does NOT start at the chart's
        // internal (0,0). The overlay rect IS positioned exactly at chart
        // (0,0) with the chart's w × h, so its rect maps correctly.
        const overlay = containerNode.querySelector('rect.skewt-overlay');
        if (!overlay) return;
        const rect = overlay.getBoundingClientRect();
        const localX = event.clientX - rect.left;
        const localY = event.clientY - rect.top;
        const p = scales.invY(localY);
        if (!Number.isFinite(p) || p < topp || p > basep) {
            hideAll();
            return;
        }
        // Mouse temperature: invert the piecewise skew at (localX, localY).
        const mouseT = scales.invSkewX
            ? scales.invSkewX(localX, localY)
            : scales.x.invert(localX);
        if (!Number.isFinite(mouseT)) {
            hideAll();
            return;
        }

        cursorLine
            .attr('y1', localY)
            .attr('y2', localY)
            .style('display', null);

        // Isohume — gray line: parcel at (mouseT, p) treated as dewpoint,
        // traced upward along constant mixing ratio.
        isohumePath
            .attr('d', lineGen(traceIsohume(mouseT, p)))
            .style('display', null);

        // Dry adiabat — solid lightgray, anchored to cursor.
        dryPath
            .attr('d', lineGen(traceDry(mouseT, p)))
            .style('display', null);

        // Moist adiabat — pink animated dashed, starts at LCL (not cursor).
        const moistTrace = traceMoist(mouseT, p);
        moistPath
            .attr('d', lineGen(moistTrace))
            .style('display', null);

        // CAPE/CIN translucent polygons between the moist parcel ascent and
        // the env profile. Where parcel > env → CAPE (red); where parcel < env
        // → CIN (blue).
        const segs = buildCapeAreas(moistTrace);
        const capeD = segs.filter((s) => s.type === 'CAPE').map(segToPolygon).filter(Boolean).join(' ');
        const cinD  = segs.filter((s) => s.type === 'CIN').map(segToPolygon).filter(Boolean).join(' ');
        if (capeD) {
            mouseCapeArea.attr('d', capeD).style('display', null);
        } else {
            mouseCapeArea.style('display', 'none');
        }
        if (cinD) {
            mouseCinArea.attr('d', cinD).style('display', null);
        } else {
            mouseCinArea.style('display', 'none');
        }
    };

    const onLeave = () => hideAll();

    containerNode.addEventListener('mousemove', onMove);
    containerNode.addEventListener('mouseleave', onLeave);

    // Stash a cleanup so subsequent attachHoverTrace calls can detach.
    containerNode.__hoverTraceCleanup = () => {
        containerNode.removeEventListener('mousemove', onMove);
        containerNode.removeEventListener('mouseleave', onLeave);
    };
}
